import type { PatchSuborderStateQuerySchema } from "../schemas";
import type { Enums } from "../types";

const STATE_ORDER_MANAGER: Array<Exclude<Enums<"orderState">, "in-progress" | "canceled">> = [
	"pending",
	"open",
];

const STATE_ORDER_PICKER: Array<Exclude<Enums<"orderState">, "in-progress" | "canceled">> = [
	"open",
	"picking",
	"complete",
];

const stateOrder = {
	manager: STATE_ORDER_MANAGER,
	picker: STATE_ORDER_PICKER,
};

/**
 * The manager's lane, widened so a membership test accepts a state the lane cannot contain.
 */
const MANAGER_STATES: Array<Enums<"orderState">> = STATE_ORDER_MANAGER;

export function getNewSuborderState(
	currentState: Exclude<Enums<"orderState">, "in-progress">,
	query: PatchSuborderStateQuerySchema,
): Exclude<Enums<"orderState">, "in-progress"> {
	if (currentState === "canceled") return currentState;
	if (query.direction && !["forward", "back"].includes(query.direction)) {
		return currentState;
	}
	if (query.direction === "forward") {
		return getNextSuborderState(currentState, query);
	} else if (query.direction === "back") {
		return getPreviousSuborderState(currentState, query);
	} else {
		return getRollingSuborderState(currentState, query);
	}
}

function getNextSuborderState(
	currentState: Exclude<Enums<"orderState">, "in-progress" | "canceled">,
	query: PatchSuborderStateQuerySchema,
): Exclude<Enums<"orderState">, "in-progress"> {
	const currentIndex = stateOrder[query.role].indexOf(currentState);
	if (currentIndex < 0) return stateOrder[query.role][0]!;
	const newIndex = Math.min(stateOrder[query.role].length - 1, currentIndex + 1);
	return stateOrder[query.role].at(newIndex)!;
}

function getPreviousSuborderState(
	currentState: Exclude<Enums<"orderState">, "in-progress" | "canceled">,
	query: PatchSuborderStateQuerySchema,
): Exclude<Enums<"orderState">, "in-progress" | "canceled"> {
	const currentIndex = stateOrder[query.role].indexOf(currentState);
	if (currentIndex < 0) return stateOrder[query.role][0]!;
	const newIndex = Math.max(0, currentIndex - 1);
	return stateOrder[query.role].at(newIndex)!;
}

function getRollingSuborderState(
	currentState: Exclude<Enums<"orderState">, "in-progress" | "canceled">,
	query: Omit<PatchSuborderStateQuerySchema, "direction">,
): Exclude<Enums<"orderState">, "in-progress" | "canceled"> {
	const currentIndex = stateOrder[query.role].indexOf(currentState);
	const newItem = stateOrder[query.role][currentIndex + 1];
	if (newItem) return newItem;
	return stateOrder[query.role][0]!;
}

export function getCanGoForwardSuborderState(
	currentState: Exclude<Enums<"orderState">, "in-progress">,
	role: PatchSuborderStateQuerySchema["role"],
) {
	if (currentState === "canceled") return false;
	const newState = getNextSuborderState(currentState, {
		direction: "forward",
		role,
	});
	return newState !== currentState;
}

export function getCanGoBackSuborderState(
	currentState: Exclude<Enums<"orderState">, "in-progress">,
	role: PatchSuborderStateQuerySchema["role"],
) {
	if (currentState === "canceled") return false;
	const newState = getPreviousSuborderState(currentState, {
		direction: "back",
		role,
	});
	return newState !== currentState;
}

export function canMoveSuborderState(
	currentState: Exclude<Enums<"orderState">, "in-progress">,
	query: PatchSuborderStateQuerySchema,
) {
	const newState = getNewSuborderState(currentState, query);
	return newState !== currentState;
}

/**
 * Whether a suborder in this state is one a manager may still release or recall.
 *
 * The manager's lane is `pending` and `open` — the two states a round passes through before a
 * picker takes it. Past `open` the state belongs to the picker's lane, and a manager's move from
 * outside the lane lands on `pending`, the first entry: a round that is being picked, or one that
 * is finished with its carriers booked out and its labels printed, would report itself as work not
 * yet started.
 *
 * The single definition of that rule, shared by both clients' release and recall controls, so
 * neither offers a move that would rewrite a picker's round.
 *
 * @param currentState - The suborder's current state.
 * @returns Whether the release and recall control applies to this round at all.
 */
export function canMoveSuborderStateAsManager(
	currentState: Exclude<Enums<"orderState">, "in-progress">,
) {
	return MANAGER_STATES.includes(currentState);
}
